import { useState, useMemo, useRef, KeyboardEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useGetIdeas, useDeleteIdea, useUpdateIdea, useGetBrainstorms, useCreateBrainstorm, useDeleteBrainstorm } from "@workspace/api-client-react";
import { format, formatDistanceToNow } from "date-fns";
import { useLocation } from "wouter";
import { useAppStore } from "@/store";
import { Archive, Plus, Trash2, PenTool, Search, X, Tag, Pencil, Check, Loader2, Lightbulb, ArrowRight, CheckCircle2, Shuffle, Mail, Share2, Briefcase, Newspaper, Radio, Send, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

interface EditingIdea {
  id: string;
  title: string;
  notes: string;
  tags: string[];
  platform: string | null;
}

type VaultTab = "vault" | "brainstorm";

export default function IdeaVault() {
  const [, setLocation] = useLocation();
  const { setIdea, setPlatform, sessionId } = useAppStore();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<VaultTab>("vault");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [editingIdea, setEditingIdea] = useState<EditingIdea | null>(null);
  const [tagInput, setTagInput] = useState("");
  const tagInputRef = useRef<HTMLInputElement>(null);
  const [remixVaultIdea, setRemixVaultIdea] = useState<{ id: string; content: string; format: string } | null>(null);
  const [vaultRemixLoading, setVaultRemixLoading] = useState(false);

  const [brainstormContent, setBrainstormContent] = useState("");
  const [brainstormError, setBrainstormError] = useState("");

  const sessionHeaders = { headers: { "x-session-id": sessionId } };

  const { data, isLoading, refetch } = useGetIdeas({ limit: 50, offset: 0 }, {
    request: sessionHeaders,
  });
  const deleteMutation = useDeleteIdea({
    mutation: {
      onSuccess: () => {
        toast({ title: "Idea deleted" });
        refetch();
      }
    },
    request: sessionHeaders,
  });

  const updateMutation = useUpdateIdea({
    mutation: {
      onSuccess: () => {
        toast({ title: "Idea updated" });
        setEditingIdea(null);
        refetch();
      },
      onError: () => {
        toast({ title: "Failed to update", variant: "destructive" });
      }
    },
    request: sessionHeaders,
  });

  const { data: brainstormsData, isLoading: brainstormsLoading, refetch: refetchBrainstorms } = useGetBrainstorms({
    request: sessionHeaders,
  });

  const createBrainstormMutation = useCreateBrainstorm({
    mutation: {
      onSuccess: () => {
        toast({ title: "Idea captured!" });
        setBrainstormContent("");
        setBrainstormError("");
        refetchBrainstorms();
      },
      onError: () => {
        toast({ title: "Failed to save", variant: "destructive" });
      },
    },
    request: sessionHeaders,
  });

  const deleteBrainstormMutation = useDeleteBrainstorm({
    mutation: {
      onSuccess: () => {
        toast({ title: "Entry deleted" });
        refetchBrainstorms();
      },
    },
    request: sessionHeaders,
  });

  const allTags = useMemo(() => {
    if (!data?.ideas) return [];
    const tagSet = new Set<string>();
    data.ideas.forEach((idea) => {
      (idea.tags ?? []).forEach((t) => tagSet.add(t));
    });
    return Array.from(tagSet).sort();
  }, [data?.ideas]);

  const filteredIdeas = useMemo(() => {
    if (!data?.ideas) return [];
    let ideas = data.ideas;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      ideas = ideas.filter(
        (i) => i.title.toLowerCase().includes(q) || i.notes.toLowerCase().includes(q)
      );
    }

    if (selectedTags.length > 0) {
      ideas = ideas.filter((i) =>
        selectedTags.every((t) => (i.tags ?? []).includes(t))
      );
    }

    return ideas;
  }, [data?.ideas, searchQuery, selectedTags]);

  const handleGenerateFromIdea = (notes: string, platform?: string | null, ideaId?: string) => {
    setIdea(notes);
    if (platform && ["twitter", "linkedin", "reddit", "instagram"].includes(platform)) {
      setPlatform(platform as "twitter" | "linkedin" | "reddit" | "instagram");
    }
    const params = new URLSearchParams();
    if (platform) params.set("platform", platform);
    params.set("autoGenerate", "1");
    setLocation(`/generator?${params.toString()}`);
    if (ideaId) {
      updateMutation.mutate({
        ideaId,
        data: { usedAt: new Date().toISOString() },
      });
    }
  };

  const handleToggleUsed = (ideaId: string, currentUsedAt?: string | null) => {
    updateMutation.mutate({
      ideaId,
      data: { usedAt: currentUsedAt ? null : new Date().toISOString() },
    });
  };

  const toggleTagFilter = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const openEdit = (idea: { id: string; title: string; notes: string; tags: string[]; platform?: string | null }) => {
    setEditingIdea({
      id: idea.id,
      title: idea.title,
      notes: idea.notes,
      tags: [...(idea.tags ?? [])],
      platform: idea.platform ?? null,
    });
  };

  const handleTagInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (editingIdea && !editingIdea.tags.includes(newTag)) {
        setEditingIdea({ ...editingIdea, tags: [...editingIdea.tags, newTag] });
      }
      setTagInput("");
    } else if (e.key === "Backspace" && !tagInput && editingIdea && editingIdea.tags.length > 0) {
      setEditingIdea({ ...editingIdea, tags: editingIdea.tags.slice(0, -1) });
    }
  };

  const removeEditTag = (tag: string) => {
    if (editingIdea) {
      setEditingIdea({ ...editingIdea, tags: editingIdea.tags.filter((t) => t !== tag) });
    }
  };

  const handleSaveEdit = () => {
    if (!editingIdea) return;
    updateMutation.mutate({
      ideaId: editingIdea.id,
      data: {
        title: editingIdea.title,
        notes: editingIdea.notes,
        tags: editingIdea.tags,
        platform: editingIdea.platform ?? undefined,
      },
    });
  };

  const handleSaveBrainstorm = () => {
    const trimmed = brainstormContent.trim();
    if (trimmed.length < 3) {
      setBrainstormError("Too short — need at least 3 characters.");
      return;
    }
    if (trimmed.length > 2000) {
      setBrainstormError("Too long — keep it under 2,000 characters.");
      return;
    }
    setBrainstormError("");
    createBrainstormMutation.mutate({ data: { content: trimmed } });
  };

  const handleBrainstormKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      handleSaveBrainstorm();
    }
  };

  const charCount = brainstormContent.trim().length;

  const handleVaultRemix = async (targetFormat: string) => {
    if (!remixVaultIdea) return;
    setVaultRemixLoading(true);
    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await fetch(`${baseUrl}/api/studio/remix`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({
          draftText: remixVaultIdea.content,
          sourceFormat: remixVaultIdea.format,
          targetFormat,
        }),
      });
      if (!res.ok) throw new Error("Remix failed.");
      const data = await res.json() as { content: string };
      setIdea(data.content);
      setRemixVaultIdea(null);
      setLocation("/studio");
      toast({ title: "Remixed!", description: `Opening in Studio as a ${targetFormat}.` });
    } catch {
      toast({ title: "Remix failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setVaultRemixLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-6xl mx-auto space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold flex items-center gap-3">
            <Archive className="w-8 h-8 text-primary" /> Idea Vault
          </h1>
          <p className="text-muted-foreground mt-2">Your personal database of raw thoughts and drafts.</p>
        </div>
        {activeTab === "vault" && (
          <div className="flex gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search ideas..."
                className="pl-9 bg-card border-border w-[200px]"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button onClick={() => setLocation("/")} className="bg-primary hover:bg-primary/90">
              <Plus className="w-4 h-4 mr-2" /> New Idea
            </Button>
          </div>
        )}
      </div>

      <div className="flex gap-1 p-1 bg-card border border-border rounded-xl w-fit">
        <button
          onClick={() => setActiveTab("vault")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === "vault"
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Archive className="w-4 h-4 inline-block mr-2 -mt-0.5" />
          Vault
        </button>
        <button
          onClick={() => setActiveTab("brainstorm")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === "brainstorm"
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Lightbulb className="w-4 h-4 inline-block mr-2 -mt-0.5" />
          Brainstorm
        </button>
      </div>

      {activeTab === "vault" ? (
        <>
          {allTags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-muted-foreground" />
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleTagFilter(tag)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    selectedTags.includes(tag)
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card border-border text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  {tag}
                </button>
              ))}
              {selectedTags.length > 0 && (
                <button
                  onClick={() => setSelectedTags([])}
                  className="text-xs text-muted-foreground hover:text-foreground ml-1"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="h-48 bg-card border border-border rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : filteredIdeas.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredIdeas.map((idea) => (
                <motion.div 
                  key={idea.id}
                  whileHover={{ y: -4 }}
                  className="card-glow bg-card border border-border rounded-2xl p-5 shadow-lg shadow-black/5 hover:border-primary/50 transition-colors flex flex-col group"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <h3 className="font-semibold text-lg line-clamp-1">{idea.title || "Untitled Idea"}</h3>
                      {(idea as { source?: string }).source && (idea as { source?: string }).source !== "manual" && (
                        <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${
                          (idea as { source?: string }).source === "generator"
                            ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
                            : (idea as { source?: string }).source === "studio"
                            ? "bg-purple-500/10 text-purple-400 border-purple-500/20"
                            : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                        }`}>
                          {(idea as { source?: string }).source === "generator" ? "Post Generator" : (idea as { source?: string }).source === "studio" ? "Studio" : (idea as { source?: string }).source === "reply" ? "Smart Reply" : (idea as { source?: string }).source}
                        </span>
                      )}
                      {idea.usedAt && (
                        <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-[10px] font-semibold rounded-full border border-emerald-500/20">
                          <CheckCircle2 className="w-3 h-3" /> Used
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleToggleUsed(idea.id, idea.usedAt)}
                        className={`p-1 transition-colors ${idea.usedAt ? "text-emerald-400 hover:text-muted-foreground" : "text-muted-foreground hover:text-emerald-400"}`}
                        aria-label={idea.usedAt ? "Mark as unused" : "Mark as used"}
                        title={idea.usedAt ? "Mark as unused" : "Mark as used"}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEdit(idea)}
                        className="text-muted-foreground hover:text-primary p-1"
                        aria-label="Edit idea"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => deleteMutation.mutate({ ideaId: idea.id })}
                        className="text-muted-foreground hover:text-destructive p-1"
                        aria-label="Delete idea"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-3 mb-3 flex-1">
                    {idea.notes}
                  </p>

                  {idea.tags && idea.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {idea.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between pt-4 border-t border-border mt-auto">
                    <div className="space-y-0.5">
                      <span className="text-xs text-muted-foreground font-medium block">
                        {format(new Date(idea.createdAt), "MMM d, yyyy")}
                      </span>
                      {idea.usedAt && (
                        <span className="text-[10px] text-emerald-400/70">
                          Used {formatDistanceToNow(new Date(idea.usedAt), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {(idea as { source?: string }).source === "studio" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-lg h-8 text-muted-foreground hover:text-primary"
                          onClick={() => setRemixVaultIdea({ id: idea.id, content: idea.notes, format: "studio" })}
                          title="Remix to another format"
                        >
                          <Shuffle className="w-3.5 h-3.5 mr-1" /> Remix
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="secondary"
                        className="rounded-lg h-8"
                        onClick={() => handleGenerateFromIdea(idea.notes, idea.platform ?? null, idea.id)}
                      >
                        <PenTool className="w-3.5 h-3.5 mr-2 text-primary" /> Generate
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-24 bg-card/50 border border-dashed border-border rounded-3xl">
              <Archive className="w-16 h-16 mx-auto text-muted-foreground opacity-50 mb-4" />
              <h3 className="text-xl font-display font-semibold mb-2">
                {searchQuery || selectedTags.length > 0 ? "No ideas match your filters" : "Your vault is empty"}
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery || selectedTags.length > 0
                  ? "Try adjusting your search or tag filters."
                  : "Capture ideas on the dashboard to save them here."}
              </p>
              {!searchQuery && selectedTags.length === 0 && (
                <Button onClick={() => setLocation("/")}>Go to Dashboard</Button>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
            <Textarea
              placeholder="Jot down a rough idea, a thought, a headline... anything. Shift+Enter to save."
              className="min-h-[120px] bg-background border-border resize-none text-base"
              value={brainstormContent}
              onChange={(e) => {
                setBrainstormContent(e.target.value);
                if (brainstormError) setBrainstormError("");
              }}
              onKeyDown={handleBrainstormKeyDown}
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`text-xs font-medium ${charCount > 2000 ? "text-destructive" : "text-muted-foreground"}`}>
                  {charCount} / 2,000
                </span>
                {brainstormError && (
                  <span className="text-xs text-destructive">{brainstormError}</span>
                )}
              </div>
              <Button
                onClick={handleSaveBrainstorm}
                disabled={createBrainstormMutation.isPending || charCount < 3}
                className="bg-primary hover:bg-primary/90"
              >
                {createBrainstormMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Plus className="w-4 h-4 mr-2" /> Capture Idea</>
                )}
              </Button>
            </div>
          </div>

          {brainstormsLoading ? (
            <div className="space-y-4">
              {[1,2,3].map(i => (
                <div key={i} className="h-24 bg-card border border-border rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : brainstormsData?.brainstorms && brainstormsData.brainstorms.length > 0 ? (
            <div className="space-y-3">
              <AnimatePresence mode="popLayout">
                {brainstormsData.brainstorms.map((entry) => (
                  <motion.div
                    key={entry.id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="card-glow bg-card border border-border rounded-2xl p-4 hover:border-primary/30 transition-colors group"
                  >
                    <p className="text-sm whitespace-pre-wrap mb-3">{entry.content}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}
                      </span>
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-muted-foreground hover:text-destructive"
                          onClick={() => deleteBrainstormMutation.mutate({ brainstormId: entry.id })}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="h-7 text-xs"
                          onClick={() => handleGenerateFromIdea(entry.content)}
                        >
                          Use for post <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="text-center py-20 bg-card/50 border border-dashed border-border rounded-3xl">
              <Lightbulb className="w-16 h-16 mx-auto text-muted-foreground opacity-50 mb-4" />
              <h3 className="text-xl font-display font-semibold mb-2">No brainstorms yet</h3>
              <p className="text-muted-foreground">Type a quick thought above and hit Capture to save it.</p>
            </div>
          )}
        </div>
      )}

      <AnimatePresence>
        {editingIdea && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setEditingIdea(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card border border-border rounded-2xl p-6 w-full max-w-lg shadow-2xl space-y-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold text-lg">Edit Idea</h3>
                <button onClick={() => setEditingIdea(null)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1 block">Title</label>
                  <Input
                    value={editingIdea.title}
                    onChange={(e) => setEditingIdea({ ...editingIdea, title: e.target.value })}
                    className="bg-background border-border"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1 block">Notes</label>
                  <Textarea
                    value={editingIdea.notes}
                    onChange={(e) => setEditingIdea({ ...editingIdea, notes: e.target.value })}
                    className="min-h-[100px] bg-background border-border"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1 block">Tags</label>
                  <div className="flex flex-wrap items-center gap-1.5 p-2 bg-background border border-border rounded-lg min-h-[40px]">
                    {editingIdea.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/15 text-primary text-xs font-medium rounded-full"
                      >
                        {tag}
                        <button onClick={() => removeEditTag(tag)} className="hover:text-destructive">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <input
                      ref={tagInputRef}
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagInputKeyDown}
                      placeholder={editingIdea.tags.length === 0 ? "Type a tag and press Enter" : ""}
                      className="flex-1 min-w-[80px] bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingIdea(null)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={updateMutation.isPending || !editingIdea.title.trim()}
                  className="flex-1 bg-primary"
                >
                  {updateMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> Save Changes</>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {remixVaultIdea && (
          <motion.div
            key="vault-remix-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setRemixVaultIdea(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 16 }}
              transition={{ duration: 0.2 }}
              className="bg-card border border-border rounded-2xl p-5 w-full max-w-sm shadow-2xl"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Shuffle className="w-4 h-4 text-primary" />
                  <p className="text-sm font-semibold text-foreground">Remix to another format</p>
                </div>
                <button onClick={() => setRemixVaultIdea(null)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Pick a target format — voice and message stay the same. The remix opens in Studio.
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: "Tweet thread", icon: Share2 },
                  { label: "Email", icon: Mail },
                  { label: "Video script", icon: Video },
                  { label: "LinkedIn post", icon: Briefcase },
                  { label: "Newsletter", icon: Newspaper },
                  { label: "Podcast intro", icon: Radio },
                  { label: "Cold email", icon: Send },
                ].map(({ label, icon: Icon }) => (
                  <button
                    key={label}
                    type="button"
                    disabled={vaultRemixLoading}
                    onClick={() => handleVaultRemix(label)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border/50 bg-secondary/20 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:bg-primary/5 hover:text-primary transition-all disabled:opacity-40"
                  >
                    {vaultRemixLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
                    → {label}
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
